import { parseFilterTree } from 'api/filter-tree'

// Immutable edits to an AND/OR filter tree (see api/filter-tree.js for its shape). Every node
// carries an `id` for React keys, and a leaf the `column` it filters; the API ignores both.

// Mirror Filterers::FilterTree, so the builder never offers a tree the API would refuse.
export const MAX_DEPTH = 4
export const MAX_NODES = 50

let sequence = 0
const nextId = () => `node-${Date.now().toString(36)}-${(sequence += 1)}`

const isGroup = (node) => Array.isArray(node?.children)

export const newLeaf = () => ({ id: nextId(), column: null, ui: {}, filter: {} })
export const newGroup = (op = 'and') => ({ id: nextId(), op, children: [newLeaf()] })
export const newTree = () => ({ id: nextId(), op: 'and', children: [] })

// Saved trees and hand-built ones may lack ids; give every node one.
export const withIds = (node) => {
  if (!node) return newTree()
  const id = node.id || nextId()
  return isGroup(node) ? { ...node, id, children: node.children.map(withIds) } : { ...node, id }
}

const mapNode = (node, id, fn) => {
  if (node.id === id) return fn(node)
  return isGroup(node) ? { ...node, children: node.children.map((child) => mapNode(child, id, fn)) } : node
}

export const updateNode = (tree, id, fn) => mapNode(tree, id, fn)

export const removeNode = (tree, id) =>
  isGroup(tree)
    ? { ...tree, children: tree.children.filter((child) => child.id !== id).map((child) => removeNode(child, id)) }
    : tree

export const addChild = (tree, groupId, child) =>
  mapNode(tree, groupId, (group) => ({ ...group, children: [...group.children, child] }))

export const countNodes = (node) =>
  isGroup(node) ? 1 + node.children.reduce((sum, child) => sum + countNodes(child), 0) : 1

// The root group is depth 1, as Filterers::FilterTree counts it.
export const depthOf = (tree, id, depth = 1) => {
  if (tree.id === id) return depth
  if (!isGroup(tree)) return null
  for (const child of tree.children) {
    const found = depthOf(child, id, depth + 1)
    if (found) return found
  }
  return null
}

export const canAddFilter = (tree, groupId) =>
  countNodes(tree) + 1 <= MAX_NODES && depthOf(tree, groupId) + 1 <= MAX_DEPTH

// A new group arrives holding one filter, which sits a level below it.
export const canAddGroup = (tree, groupId) =>
  countNodes(tree) + 2 <= MAX_NODES && depthOf(tree, groupId) + 2 <= MAX_DEPTH

export const countFilters = (node) =>
  !node ? 0 : isGroup(node) ? node.children.reduce((sum, child) => sum + countFilters(child), 0) : 1

// A saved tree ready to edit, opened with one empty filter when there is nothing in it yet.
export const startingTree = (saved) => {
  const tree = withIds(parseFilterTree(saved))
  return tree.children.length ? tree : addChild(tree, tree.id, newLeaf())
}
